import React, { useCallback, useState, useEffect } from 'react';
import { TLoginButton, TLoginButtonSize } from 'react-telegram-auth';
import { useAuth } from '../contexts/AuthContext';
import { User, TelegramAuthResponse, type BackendUser } from '../types';
import { useApiCall } from '../hooks/useApiCall';
import { isMobileUserAgent } from '../utils/mobileUtils';
import { authLogger } from '../utils/logger';
import { getServiceHeaders } from '../utils/serviceUtils';
import { sessionGet } from '../utils/storage';
import { API_BASE } from '../utils/api';

interface TelegramAuthProps {
  /** Имя бота для виджета `react-telegram-auth` (@BotFather, без `@`). Если пусто — только вход по коду. */
  botName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

/** Кнопка виджета Telegram Login временно скрыта — вход только по коду. Вернуть: `true`. */
const TELEGRAM_WIDGET_ENABLED = false;

const normalizeApiBase = (raw: string) => raw.trim().replace(/\/$/, '');

function backendUserToTelegramUser(backend: BackendUser): User {
  const tgUnique = backend?.connection?.telegram;
  const tid = tgUnique != null && tgUnique !== '' ? Number.parseInt(String(tgUnique), 10) : Number.NaN;
  return {
    id: Number.isFinite(tid) ? tid : (backend.uid ?? 'unknown'),
    name: (backend.name ?? '').trim() || 'Telegram User',
    avatar: backend.photo ?? '',
    platform: 'telegram',
  };
}

const TelegramAuth: React.FC<TelegramAuthProps> = ({ botName = '', onSuccess, onCancel }: TelegramAuthProps) => {
  const { login, setLoading, setError, sendToParent, redirectToReturnUrl } = useAuth();
  const { execute } = useApiCall();
  const [showAccountHelp, setShowAccountHelp] = useState(false);
  const [hasParentWindow, setHasParentWindow] = useState(true);
  const [hasReturnUrl, setHasReturnUrl] = useState(false);
  const [loginCode, setLoginCode] = useState('');

  const widgetBotUsername = botName.trim();
  const apiBase = normalizeApiBase(API_BASE);
  const codeBotUsername = (import.meta.env.VITE_TELEGRAM_LOGIN_CODE_BOT_USERNAME || '').trim();
  const loginCodeExplicit =
    import.meta.env.VITE_TELEGRAM_LOGIN_CODE === 'true' || import.meta.env.VITE_TELEGRAM_LOGIN_CODE === '1';
  const loginCodeEnabled = loginCodeExplicit || codeBotUsername.length > 0;
  const telegramBotDeepLink = codeBotUsername ? `https://telegram.me/${codeBotUsername}?start=sso` : null;

  useEffect(() => {
    const hasOpener = !!window.opener;
    const hasParent = window.parent !== window;
    const parentAvailable = hasOpener || hasParent;
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

    setHasParentWindow(parentAvailable);
    setHasReturnUrl(!!returnUrl);

    authLogger.info('Проверка родительского окна (Telegram)', {
      hasOpener,
      hasParent,
      parentAvailable,
      hasReturnUrl: !!returnUrl,
      loginCodeEnabled,
      widgetBot: !!widgetBotUsername,
    });
  }, [loginCodeEnabled, widgetBotUsername]);

  const fetchBackendUser = useCallback(
    async (token: string): Promise<BackendUser> => {
      const userResponse = await execute({
        url: `${apiBase}/user`,
        method: 'GET',
        headers: {
          'ssmm-token': token,
          ...getServiceHeaders(),
        },
      });
      if (!userResponse) {
        throw new Error('Не удалось загрузить профиль пользователя');
      }
      return userResponse as BackendUser;
    },
    [apiBase, execute]
  );

  const completeLogin = useCallback(
    async (token: string, user: User, backendUser: BackendUser, options?: { clearCode?: boolean }) => {
      if (options?.clearCode) {
        setLoginCode('');
      }
      login(user, token, backendUser);

      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

      if (returnUrl && isMobileUserAgent()) {
        redirectToReturnUrl(token);
      } else {
        sendToParent({
          type: 'AUTH_SUCCESS',
          data: {
            token,
            user: backendUser,
          },
        });
      }

      onSuccess?.();
    },
    [login, redirectToReturnUrl, sendToParent, onSuccess]
  );

  const telegramAuth = useCallback(
    async (data: TelegramAuthResponse) => {
      if (!widgetBotUsername) {
        setError('Виджет Telegram не настроен (пустой VITE_TELEGRAM_BOT_NAME / botName)');
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const result = await execute({
          url: `${apiBase}/oauth/telegram/callback/${widgetBotUsername}?${new URLSearchParams(data as any).toString()}`,
          method: 'GET',
          headers: {
            ...getServiceHeaders(),
          },
        });

        if (!result?.token) {
          throw new Error('No token received from Telegram auth');
        }

        const backendUser = await fetchBackendUser(result.token);
        const user: User = {
          id: data.id,
          name: `${data.first_name} ${data.last_name || ''}`.trim(),
          avatar: data.photo_url,
          platform: 'telegram',
        };

        await completeLogin(result.token, user, backendUser);
      } catch (e: unknown) {
        const errorMessage =
          (e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
            ? (e as Error).message
            : null) ||
          (typeof e === 'string' ? e : '') ||
          'Telegram authentication failed';
        setError(errorMessage);
        sendToParent({
          type: 'AUTH_ERROR',
          error: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      apiBase,
      completeLogin,
      execute,
      fetchBackendUser,
      sendToParent,
      setError,
      setLoading,
      widgetBotUsername,
    ]
  );

  const submitLoginCode = useCallback(async () => {
    if (!loginCodeEnabled) {
      setError('Вход по коду выключен. Задайте VITE_TELEGRAM_LOGIN_CODE=true или VITE_TELEGRAM_LOGIN_CODE_BOT_USERNAME.');
      return;
    }
    const normalized = loginCode.replace(/\D/g, '').slice(0, 5);
    if (normalized.length !== 5) {
      setError('Введите 5 цифр кода из Telegram');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const qs = new URLSearchParams(window.location.search);
      const utmRaw = qs.get('utm') || qs.get('utm_string') || '';
      const loginCodeUrl =
        `${apiBase}/oauth/telegram/login-code` +
        (utmRaw ? `?${new URLSearchParams({ utm: utmRaw }).toString()}` : '');

      const result = await execute({
        url: loginCodeUrl,
        method: 'POST',
        data: { code: normalized },
        headers: {
          ...getServiceHeaders(),
        },
      });

      if (!result?.token) {
        throw new Error('Не получили токен от сервера');
      }

      const backendUser = await fetchBackendUser(result.token);
      const user = backendUserToTelegramUser(backendUser);
      await completeLogin(result.token, user, backendUser, { clearCode: true });
    } catch (e: unknown) {
      const errorMessage =
        (e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : null) || 'Ошибка входа по коду';
      setError(errorMessage);
      sendToParent({
        type: 'AUTH_ERROR',
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [
    apiBase,
    completeLogin,
    execute,
    fetchBackendUser,
    loginCode,
    loginCodeEnabled,
    sendToParent,
    setError,
    setLoading,
  ]);

  const handleCancel = () => {
    onCancel?.();
  };

  const showWidget = TELEGRAM_WIDGET_ENABLED && widgetBotUsername.length > 0;
  const showAnyMethod = showWidget || loginCodeEnabled;

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={handleCancel}
          className="flex items-center mb-6 transition-colors text-zinc-500 hover:text-zinc-300"
          type="button"
        >
          <svg className="mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>

        <div className="flex items-center mb-2 space-x-3">
          <div className="w-10 h-10 rounded-xl bg-[#27A7E7] flex items-center justify-center">
            <svg className="w-5 h-5 text-on-accent" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Telegram</h1>
            <p className="text-xs text-zinc-500">Вход через мессенджер</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {!hasParentWindow && !hasReturnUrl && (
          <div className="p-3 mb-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-amber-400">
                Страница открыта напрямую. Для корректной работы откройте через приложение.
              </p>
            </div>
          </div>
        )}

        {showAnyMethod ? (
          <div className="space-y-4">
            {showWidget ? (
              <div className="flex justify-center py-4">
                <TLoginButton
                  botName={widgetBotUsername}
                  buttonSize={TLoginButtonSize.Large}
                  cornerRadius={12}
                  lang="ru"
                  requestAccess="write"
                  usePic={false}
                  onAuthCallback={(user) => {
                    authLogger.info('Telegram callback получен', { userId: user.id, userName: user.first_name });
                    void telegramAuth(user);
                  }}
                />
              </div>
            ) : TELEGRAM_WIDGET_ENABLED ? (
              <p className="text-xs text-center text-zinc-500">
                Виджет входа через Telegram Login не подключён. Используйте вход по коду ниже или задайте{' '}
                <code className="text-zinc-400">VITE_TELEGRAM_BOT_NAME</code>.
              </p>
            ) : null}

            {loginCodeEnabled ? (
              <div className="p-4 space-y-3 rounded-xl border bg-zinc-900 border-zinc-800">
                <p className="text-xs font-medium text-zinc-300">Вход по коду</p>
                <p className="text-xs leading-relaxed text-zinc-500">
                  {telegramBotDeepLink ? (
                    <>
                      Нажмите «Открыть бота», затем в Telegram отправьте <span className="text-zinc-400">/start</span> или{' '}
                      <span className="text-zinc-400">/code</span> — придёт одноразовый код. Введите его ниже.
                    </>
                  ) : (
                    <>
                      В Telegram отправьте боту авторизации команду <span className="text-zinc-400">/start</span> или{' '}
                      <span className="text-zinc-400">/code</span>, затем введите присланный код.
                    </>
                  )}
                </p>
                {telegramBotDeepLink ? (
                  <a
                    href={telegramBotDeepLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex justify-center items-center w-full py-2.5 text-sm font-medium text-white rounded-lg bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 transition-colors"
                  >
                    Открыть бота для получения кода
                  </a>
                ) : null}
                <input
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="text"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  onKeyDown={(e) => e.key === 'Enter' && void submitLoginCode()}
                  placeholder="00000"
                  aria-label="Код входа из Telegram"
                  className="w-full px-3 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#27A7E7]/40"
                />
                <button
                  type="button"
                  onClick={() => void submitLoginCode()}
                  className="w-full py-2.5 rounded-lg bg-[#27A7E7] text-on-accent text-sm font-medium hover:bg-[#229ad1] transition-colors"
                >
                  Войти по коду
                </button>
              </div>
            ) : null}

            {showWidget ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowAccountHelp(!showAccountHelp)}
                  className="w-full text-xs text-center transition-colors text-zinc-500 hover:text-zinc-400"
                >
                  {showAccountHelp ? 'Скрыть подсказку' : 'Войти с другого аккаунта?'}
                </button>

                {showAccountHelp && (
                  <div className="p-4 space-y-3 rounded-xl border bg-zinc-900 border-zinc-800">
                    <p className="text-xs font-medium text-zinc-400">Способы смены аккаунта:</p>

                    <div className="p-3 rounded-lg bg-zinc-800/50">
                      <p className="text-xs font-medium text-[#27A7E7] mb-1.5">📱 Через Telegram</p>
                      <ol className="text-xs text-zinc-500 space-y-0.5 list-decimal list-inside">
                        <li>Настройки → Устройства</li>
                        <li>Завершить все другие сеансы</li>
                        <li>Вернуться и авторизоваться</li>
                      </ol>
                    </div>

                    <div className="p-3 rounded-lg bg-zinc-800/50">
                      <p className="text-xs font-medium text-purple-400 mb-1.5">🍪 Очистить cookies</p>
                      <p className="text-xs text-zinc-500">
                        Удалите cookies для{' '}
                        <code className="px-1 py-0.5 rounded bg-zinc-700 text-zinc-400">oauth.telegram.org</code>
                      </p>
                    </div>

                    <div className="p-3 rounded-lg bg-zinc-800/50">
                      <p className="text-xs text-zinc-500">
                        🕵️ Или откройте в режиме <span className="text-zinc-400">инкогнито</span>
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        ) : (
          <div className="p-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
            <p className="text-sm font-medium text-amber-400">Ошибка конфигурации</p>
            <p className="mt-1 text-xs text-amber-400/70">
              Задайте <code className="px-1 py-0.5 rounded bg-amber-500/15">VITE_TELEGRAM_BOT_NAME</code> для виджета, и/или
              включите вход по коду: <code className="px-1 py-0.5 rounded bg-amber-500/15">VITE_TELEGRAM_LOGIN_CODE=true</code> или{' '}
              <code className="px-1 py-0.5 rounded bg-amber-500/15">VITE_TELEGRAM_LOGIN_CODE_BOT_USERNAME</code> (ссылка на бота +
              имя для кнопки).
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramAuth;
